import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamMembersAPI, leaveTypesAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format } from 'date-fns';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Award,
  Shield,
  Calendar,
  Plus,
  X,
  FileText,
  CheckCircle,
  AlertTriangle,
  Upload,
  Clock,
  Package,
  Edit2,
  Trash2,
} from 'lucide-react';

const TeamMemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // PPE State
  const [ppeData, setPpeData] = useState({ items: [], total_value: 0 });
  const [showPPEModal, setShowPPEModal] = useState(false);
  const [ppeForm, setPpeForm] = useState({
    description: '',
    quantity: 1,
    rate: 0,
    date_issued: new Date().toISOString().split('T')[0],
    notes: '',
  });
  
  // Certifications State
  const [certifications, setCertifications] = useState({ certifications: [], has_valid_medical: false });
  const [showCertModal, setShowCertModal] = useState(false);
  const [certForm, setCertForm] = useState({
    description: '',
    certification_type: 'general',
    cost: 0,
    date_issued: new Date().toISOString().split('T')[0],
    date_expiry: '',
    notes: '',
  });
  
  // Leave State
  const [leaveData, setLeaveData] = useState({ records: [], total_leave_days: 0, by_type: {} });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'Annual Leave',
    description: '',
    start_date: '',
    end_date: '',
    notes: '',
  });
  
  // Edit mode state
  const [editingPPE, setEditingPPE] = useState(null);
  const [editingCert, setEditingCert] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [memberRes, summaryRes, ppeRes, certRes, leaveRes, leaveTypesRes] = await Promise.all([
        teamMembersAPI.getOne(id),
        teamMembersAPI.getSummary(id),
        teamMembersAPI.getPPE(id),
        teamMembersAPI.getCertifications(id),
        teamMembersAPI.getLeave(id),
        leaveTypesAPI.getAll(),
      ]);
      setMember(memberRes.data);
      setSummary(summaryRes.data);
      setPpeData(ppeRes.data);
      setCertifications(certRes.data);
      setLeaveData(leaveRes.data);
      setLeaveTypes(leaveTypesRes.data);
    } catch (error) {
      toast.error('Failed to load team member details');
      navigate('/team-members');
    } finally {
      setLoading(false);
    }
  };

  const handleIssuePPE = async () => {
    if (!ppeForm.description) {
      toast.error('Please enter a description');
      return;
    }
    try {
      await teamMembersAPI.issuePPE(id, ppeForm);
      toast.success('PPE issued successfully');
      setShowPPEModal(false);
      setPpeForm({ description: '', quantity: 1, rate: 0, date_issued: new Date().toISOString().split('T')[0], notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to issue PPE');
    }
  };

  const handleAddCertification = async () => {
    if (!certForm.description) {
      toast.error('Please enter a description');
      return;
    }
    try {
      await teamMembersAPI.addCertification(id, certForm);
      toast.success('Certification added successfully');
      setShowCertModal(false);
      setCertForm({ description: '', certification_type: 'general', cost: 0, date_issued: new Date().toISOString().split('T')[0], date_expiry: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add certification');
    }
  };

  const handleAddLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) {
      toast.error('Please enter start and end dates');
      return;
    }
    try {
      const result = await teamMembersAPI.addLeave(id, leaveForm);
      const conflicts = result.data.conflicts || [];
      if (conflicts.length > 0) {
        toast.warning(`Leave recorded: ${result.data.days_count} day(s). Warning: ${conflicts.length} job conflict(s) detected!`, {
          duration: 5000
        });
      } else {
        toast.success(`Leave recorded: ${result.data.days_count} day(s)`);
      }
      setShowLeaveModal(false);
      setLeaveForm({ leave_type: 'Annual Leave', description: '', start_date: '', end_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to record leave');
    }
  };

  // Edit PPE
  const handleEditPPE = (item) => {
    setEditingPPE(item);
    setPpeForm({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      date_issued: item.date_issued?.split('T')[0] || '',
      notes: item.notes || '',
    });
    setShowPPEModal(true);
  };

  const handleUpdatePPE = async () => {
    if (!editingPPE) return;
    try {
      await teamMembersAPI.updatePPE(id, editingPPE.id, ppeForm);
      toast.success('PPE record updated');
      setShowPPEModal(false);
      setEditingPPE(null);
      setPpeForm({ description: '', quantity: 1, rate: 0, date_issued: new Date().toISOString().split('T')[0], notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update PPE record');
    }
  };

  const handleDeletePPE = async (ppeId) => {
    if (!window.confirm('Are you sure you want to delete this PPE record?')) return;
    try {
      await teamMembersAPI.deletePPE(id, ppeId);
      toast.success('PPE record deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete PPE record');
    }
  };

  // Edit Certification
  const handleEditCert = (cert) => {
    setEditingCert(cert);
    setCertForm({
      description: cert.description,
      certification_type: cert.certification_type,
      cost: cert.cost,
      date_issued: cert.date_issued?.split('T')[0] || '',
      date_expiry: cert.date_expiry?.split('T')[0] || '',
      notes: cert.notes || '',
    });
    setShowCertModal(true);
  };

  const handleUpdateCert = async () => {
    if (!editingCert) return;
    try {
      await teamMembersAPI.updateCertification(id, editingCert.id, certForm);
      toast.success('Certification updated');
      setShowCertModal(false);
      setEditingCert(null);
      setCertForm({ description: '', certification_type: 'general', cost: 0, date_issued: new Date().toISOString().split('T')[0], date_expiry: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update certification');
    }
  };

  const handleDeleteCert = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) return;
    try {
      await teamMembersAPI.deleteCertification(id, certId);
      toast.success('Certification deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete certification');
    }
  };

  // Edit Leave
  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setLeaveForm({
      leave_type: leave.leave_type,
      description: leave.description || '',
      start_date: leave.start_date?.split('T')[0] || '',
      end_date: leave.end_date?.split('T')[0] || '',
      notes: leave.notes || '',
    });
    setShowLeaveModal(true);
  };

  const handleUpdateLeave = async () => {
    if (!editingLeave) return;
    try {
      const result = await teamMembersAPI.updateLeave(id, editingLeave.id, leaveForm);
      toast.success(`Leave updated: ${result.data.days_count} day(s)`);
      setShowLeaveModal(false);
      setEditingLeave(null);
      setLeaveForm({ leave_type: 'Annual Leave', description: '', start_date: '', end_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update leave record');
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave record?')) return;
    try {
      await teamMembersAPI.deleteLeave(id, leaveId);
      toast.success('Leave record deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete leave record');
    }
  };

  // Reset modal state when closing
  const closePPEModal = () => {
    setShowPPEModal(false);
    setEditingPPE(null);
    setPpeForm({ description: '', quantity: 1, rate: 0, date_issued: new Date().toISOString().split('T')[0], notes: '' });
  };

  const closeCertModal = () => {
    setShowCertModal(false);
    setEditingCert(null);
    setCertForm({ description: '', certification_type: 'general', cost: 0, date_issued: new Date().toISOString().split('T')[0], date_expiry: '', notes: '' });
  };

  const closeLeaveModal = () => {
    setShowLeaveModal(false);
    setEditingLeave(null);
    setLeaveForm({ leave_type: 'Annual Leave', description: '', start_date: '', end_date: '', notes: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="p-6">
        <p className="text-slate-500">Team member not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/team-members')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{member.name}</h1>
            <p className="text-slate-600">{member.role?.charAt(0).toUpperCase() + member.role?.slice(1)} • {member.employee_id || 'No ID'}</p>
          </div>
        </div>
        
        {/* Summary Badges */}
        <div className="flex items-center gap-3">
          <div className={`px-3 py-2 rounded-lg flex items-center gap-2 ${summary?.has_valid_medical ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {summary?.has_valid_medical ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span className="text-sm font-medium">{summary?.has_valid_medical ? 'Valid Medical' : 'No Medical'}</span>
          </div>
          <div className="px-3 py-2 rounded-lg bg-blue-100 text-blue-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">{summary?.annual_leave_days || 0} Leave Days ({summary?.annual_leave_year})</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {['overview', 'ppe', 'certifications', 'leave'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab === 'ppe' ? 'PPE Issues' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Contact Information
            </h3>
            <div className="space-y-3">
              {member.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{member.phone}</span>
                </div>
              )}
              {member.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{member.email}</span>
                </div>
              )}
              {member.address && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>{member.address}</span>
                </div>
              )}
              {member.job_description && (
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <span>{member.job_description}</span>
                </div>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{summary?.total_certifications || 0}</p>
                <p className="text-xs text-slate-500">Certifications</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{summary?.total_ppe_items || 0}</p>
                <p className="text-xs text-slate-500">PPE Items</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{summary?.annual_leave_days || 0}</p>
                <p className="text-xs text-slate-500">Leave Days</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary?.total_ppe_value || 0)}</p>
                <p className="text-xs text-slate-500">PPE Value</p>
              </div>
            </div>
          </div>

          {/* Expiring Soon */}
          {summary?.certifications_expiring_soon?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <h3 className="font-semibold text-yellow-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Expiring Soon
              </h3>
              <div className="space-y-2">
                {summary.certifications_expiring_soon.map((cert, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-yellow-700">{cert.description}</span>
                    <span className="font-medium text-yellow-800">{cert.days_until_expiry} days</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PPE Tab */}
      {activeTab === 'ppe' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">PPE Issues</h3>
              <p className="text-sm text-slate-600">{ppeData.total_items || 0} items • Total: {formatCurrency(ppeData.total_value || 0)}</p>
            </div>
            {canEdit && (
              <button
                onClick={() => setShowPPEModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Issue PPE
              </button>
            )}
          </div>
          <div className="p-6">
            {ppeData.items?.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total</th>
                    {canEdit && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {ppeData.items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {item.date_issued ? format(new Date(item.date_issued), 'dd MMM yyyy') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.description}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{formatCurrency(item.rate)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 text-right">{formatCurrency(item.total)}</td>
                      {canEdit && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleEditPPE(item)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded mr-1"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePPE(item.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No PPE issued yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Certifications Tab */}
      {activeTab === 'certifications' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Certifications</h3>
              <p className="text-sm text-slate-600">
                {certifications.total_certifications || 0} certifications • 
                {certifications.has_valid_medical ? ' ✓ Valid Medical' : ' ✗ No Valid Medical'}
              </p>
            </div>
            {canEdit && (
              <button
                onClick={() => setShowCertModal(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Certification
              </button>
            )}
          </div>
          <div className="p-6">
            {certifications.certifications?.length > 0 ? (
              <div className="space-y-4">
                {certifications.certifications.map((cert) => (
                  <div key={cert.id} className={`border rounded-lg p-4 ${cert.is_valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            cert.certification_type === 'medical' ? 'bg-blue-100 text-blue-700' :
                            cert.certification_type === 'safety' ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {cert.certification_type}
                          </span>
                          <h4 className="font-medium text-slate-900">{cert.description}</h4>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                          <span>Issued: {cert.date_issued ? format(new Date(cert.date_issued), 'dd MMM yyyy') : '-'}</span>
                          {cert.date_expiry && (
                            <span>Expires: {format(new Date(cert.date_expiry), 'dd MMM yyyy')}</span>
                          )}
                          {cert.cost > 0 && <span>Cost: {formatCurrency(cert.cost)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`flex items-center gap-1 ${cert.is_valid ? 'text-green-600' : 'text-red-600'}`}>
                          {cert.is_valid ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                          <span className="text-sm font-medium">
                            {cert.is_valid ? (cert.days_until_expiry != null ? `${cert.days_until_expiry} days` : 'Valid') : 'Expired'}
                          </span>
                        </div>
                        {canEdit && (
                          <div className="flex items-center">
                            <button
                              onClick={() => handleEditCert(cert)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCert(cert.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No certifications recorded</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leave Tab */}
      {activeTab === 'leave' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Leave Records ({leaveData.year})</h3>
              <p className="text-sm text-slate-600">
                Total: {leaveData.total_leave_days || 0} days
                {Object.entries(leaveData.by_type || {}).map(([type, days]) => (
                  <span key={type} className="ml-2">• {type}: {days}</span>
                ))}
              </p>
            </div>
            {canEdit && (
              <button
                onClick={() => setShowLeaveModal(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Record Leave
              </button>
            )}
          </div>
          <div className="p-6">
            {leaveData.records?.length > 0 ? (
              <div className="space-y-4">
                {leaveData.records.map((leave) => (
                  <div key={leave.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {leave.leave_type}
                          </span>
                          <span className="text-sm font-medium text-slate-900">{leave.days_count} day(s)</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {leave.start_date ? format(new Date(leave.start_date), 'dd MMM yyyy') : ''} - {leave.end_date ? format(new Date(leave.end_date), 'dd MMM yyyy') : ''}
                        </p>
                        {leave.description && <p className="mt-1 text-sm text-slate-500">{leave.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {leave.document_url && (
                          <a
                            href={leave.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            Document
                          </a>
                        )}
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleEditLeave(leave)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLeave(leave.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No leave records for this year</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PPE Modal */}
      {showPPEModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{editingPPE ? 'Edit PPE' : 'Issue PPE'}</h2>
              <button onClick={closePPEModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <input
                  type="text"
                  value={ppeForm.description}
                  onChange={(e) => setPpeForm({ ...ppeForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Safety Helmet, Gloves"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Quantity</label>
                  <input
                    type="text"
                    value={ppeForm.quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setPpeForm({ ...ppeForm, quantity: value ? parseInt(value) : 1 });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Rate (R)</label>
                  <input
                    type="text"
                    value={ppeForm.rate_str !== undefined ? ppeForm.rate_str : ppeForm.rate}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setPpeForm({ ...ppeForm, rate_str: value, rate: value ? parseFloat(value) : 0 });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-medium">{formatCurrency(ppeForm.quantity * ppeForm.rate)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-600">Total:</span>
                  <span className="font-bold text-slate-900">{formatCurrency(ppeForm.quantity * ppeForm.rate)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date Issued</label>
                <input
                  type="date"
                  value={ppeForm.date_issued}
                  onChange={(e) => setPpeForm({ ...ppeForm, date_issued: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={ppeForm.notes}
                  onChange={(e) => setPpeForm({ ...ppeForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={closePPEModal} className="px-4 py-2 border border-slate-200 rounded-lg">Cancel</button>
                <button 
                  onClick={editingPPE ? handleUpdatePPE : handleIssuePPE} 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  {editingPPE ? 'Update PPE' : 'Issue PPE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certification Modal */}
      {showCertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{editingCert ? 'Edit Certification' : 'Add Certification'}</h2>
              <button onClick={closeCertModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <input
                  type="text"
                  value={certForm.description}
                  onChange={(e) => setCertForm({ ...certForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Medical Certificate, Working at Heights"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                  <select
                    value={certForm.certification_type}
                    onChange={(e) => setCertForm({ ...certForm, certification_type: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="medical">Medical</option>
                    <option value="safety">Safety</option>
                    <option value="trade">Trade</option>
                    <option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cost (R)</label>
                  <input
                    type="text"
                    value={certForm.cost_str !== undefined ? certForm.cost_str : certForm.cost}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setCertForm({ ...certForm, cost_str: value, cost: value ? parseFloat(value) : 0 });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date Issued</label>
                  <input
                    type="date"
                    value={certForm.date_issued}
                    onChange={(e) => setCertForm({ ...certForm, date_issued: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date Expiry</label>
                  <input
                    type="date"
                    value={certForm.date_expiry}
                    onChange={(e) => setCertForm({ ...certForm, date_expiry: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={certForm.notes}
                  onChange={(e) => setCertForm({ ...certForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={closeCertModal} className="px-4 py-2 border border-slate-200 rounded-lg">Cancel</button>
                <button 
                  onClick={editingCert ? handleUpdateCert : handleAddCertification} 
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                >
                  {editingCert ? 'Update Certification' : 'Add Certification'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{editingLeave ? 'Edit Leave' : 'Record Leave'}</h2>
              <button onClick={closeLeaveModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Leave Type *</label>
                <select
                  value={leaveForm.leave_type}
                  onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {leaveTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <input
                  type="text"
                  value={leaveForm.description}
                  onChange={(e) => setLeaveForm({ ...leaveForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Reason for leave"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">First Date of Leave *</label>
                  <input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Return to Work Date *</label>
                  <input
                    type="date"
                    value={leaveForm.end_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={leaveForm.notes}
                  onChange={(e) => setLeaveForm({ ...leaveForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={closeLeaveModal} className="px-4 py-2 border border-slate-200 rounded-lg">Cancel</button>
                <button 
                  onClick={editingLeave ? handleUpdateLeave : handleAddLeave} 
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  {editingLeave ? 'Update Leave' : 'Record Leave'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberDetail;
